import { DatePicker, Label } from '@loadsmart/loadsmart-ui'
import { Checkbox, Dialog, Field, Layout, Text, TextField } from '@loadsmart/miranda-react'
import { useCallback, useState } from 'react'
import { toCSSValue } from '@loadsmart/miranda-tokens'
import styled from 'styled-components'

import { useAsset } from '../hooks/useAsset'
import { useMessage } from '../../hooks/useMessage'

type OutgateAssetDialogProps = {
  isOpen: boolean
  handleClose: () => void
  handleAssetDrawerClose: () => void
  asset: any
  refresh: () => void
}

const StyledLabel = styled(Label)`
  > span {
    color: ${toCSSValue('color-text-tertiary')};
  }
`

export function OutgateAssetDialog({
  isOpen,
  handleClose,
  handleAssetDrawerClose,
  asset,
  refresh,
}: OutgateAssetDialogProps) {
  const messenger = useMessage()

  const { outgateObservation } = useAsset({
    onOutgateSuccess: () => {
      messenger.success('Asset outgated')
      refresh()
    },
    onOutgateError: () => {
      messenger.error(new Error('Something went wrong, could not outgate asset'))
      setIsLoading(false)
    },
  })

  const canOutgateVehicle = !asset?.vehicleOutwardObservationId && asset?.vehicleId
  const canOutgateChassis = !asset?.chassisOutwardObservationId && asset?.chassisId
  const canOutgateLoad = !asset?.loadOutwardObservationId && asset?.loadId

  const [isLoading, setIsLoading] = useState(false)
  const [outgateVehicle, setOutgateVehicle] = useState(Boolean(canOutgateVehicle))
  const [outgateChassis, setOutgateChassis] = useState(Boolean(canOutgateChassis))
  const [outgateLoad, setOutgateLoad] = useState(Boolean(canOutgateLoad))

  const currentDate = new Date()
  const currentTime = currentDate.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })

  const [date, setDate] = useState<string | null>(currentDate.toISOString())
  const [time, setTime] = useState(currentTime)

  const hasOutgateSelection = outgateVehicle || outgateChassis || outgateLoad
  const canSubmit = date && time && hasOutgateSelection

  const handleOutgateAsset = useCallback(async (): Promise<void> => {
    if (date) {
      setIsLoading(true)

      const newDate = new Date(date)
      newDate.setHours(Number(time.split(':')[0]))
      newDate.setMinutes(Number(time.split(':')[1]))

      const outgateDateTime = newDate.toUTCString()

      const outgateAssetObject = {
        ...asset,
        vehicleId: outgateVehicle && canOutgateVehicle ? asset.vehicleId : null,
        chassisId: outgateChassis && canOutgateChassis ? asset.chassisId : null,
        loadId: outgateLoad && canOutgateLoad ? asset.loadId : null,
      }

      try {
        await outgateObservation(outgateAssetObject.id, outgateAssetObject, outgateDateTime, {})
        setIsLoading(false)
        handleClose()
        handleAssetDrawerClose()
      } catch (e) {
        setIsLoading(false)
      }
    }
  }, [
    outgateVehicle,
    outgateChassis,
    outgateLoad,
    canOutgateVehicle,
    canOutgateChassis,
    canOutgateLoad,
    date,
    time,
  ])

  return (
    <Dialog open={isOpen} onClose={handleClose} size="small">
      <Dialog.Header>
        <Text variant="heading-md-bold">Outgate asset</Text>
        <Dialog.Close />
      </Dialog.Header>

      <Dialog.Body>
        <Layout.Stack>
          <Text>Select which assets from the ingate transaction you would like to outgate</Text>

          {asset?.vehicleId && (
            <Checkbox
              name="tractor"
              checked={outgateVehicle}
              onChange={() => setOutgateVehicle(!outgateVehicle)}
              disabled={!canOutgateVehicle}
            >
              <Text color="color-text-tertiary">
                {asset.vehicleTypeName || asset?.vehicle?.type?.name} -{' '}
                {asset.vehicleNumber || asset?.vehicle?.number || 'Not provided'}
              </Text>
            </Checkbox>
          )}
          {asset?.chassisId && (
            <Checkbox
              name="chassis"
              checked={outgateChassis}
              onChange={() => setOutgateChassis(!outgateChassis)}
              disabled={!canOutgateChassis}
            >
              <Text color="color-text-tertiary">
                Chassis - {asset.chassisNumber || asset?.chassis?.number || 'Not provided'}
              </Text>
            </Checkbox>
          )}
          {asset?.loadId && (
            <Checkbox
              name="load"
              checked={outgateLoad}
              onChange={() => setOutgateLoad(!outgateLoad)}
              disabled={!canOutgateLoad}
            >
              <Text color="color-text-tertiary">
                {asset.loadType || asset?.load?.type?.name} -{' '}
                {asset.loadNumber || asset?.load?.number || 'Not provided'}
              </Text>
            </Checkbox>
          )}

          <Text>Enter the outgate date and time</Text>

          <Layout.Grid>
            <Field>
              <Layout.Stack gap="spacing-2">
                <StyledLabel>Outgate date</StyledLabel>
                <DatePicker
                  name="outgateDate"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </Layout.Stack>
            </Field>
            <Field>
              <Layout.Stack gap="spacing-2">
                <StyledLabel>Outgate time</StyledLabel>
                <TextField
                  name="outgateTime"
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                />
              </Layout.Stack>
            </Field>
          </Layout.Grid>
        </Layout.Stack>
      </Dialog.Body>

      <Dialog.Actions>
        <Dialog.ActionSecondary disabled={isLoading} onClick={handleClose}>
          Cancel
        </Dialog.ActionSecondary>
        <Dialog.ActionPrimary disabled={isLoading || !canSubmit} onClick={handleOutgateAsset}>
          Outgate
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </Dialog>
  )
}
